import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Clock, AlertTriangle, Zap, Crown, Star, Check, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Banner de contagem regressiva do trial
export const TrialBanner = ({ user }) => {
  const [trialInfo, setTrialInfo] = useState(null);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    fetchTrialStatus();
    const interval = setInterval(fetchTrialStatus, 60000); // Atualiza a cada minuto
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!trialInfo?.total_seconds_left) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        const totalSeconds = trialInfo.total_seconds_left - 1;
        if (totalSeconds <= 0) {
          clearInterval(timer);
          return { days: 0, hours: 0, minutes: 0, seconds: 0 };
        }
        
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        
        return { days, hours, minutes, seconds };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [trialInfo]);

  const fetchTrialStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/trial/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrialInfo(response.data);
      
      // Calcular tempo restante inicial
      const totalSeconds = response.data.total_seconds_left || 0;
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      setTimeLeft({ days, hours, minutes, seconds });
      
    } catch (error) {
      console.error('Error fetching trial status:', error);
    }
  };

  if (!trialInfo?.is_trial) return null;

  const isUrgent = timeLeft.days <= 2;
  const isExpired = trialInfo.trial_expired;

  if (isExpired) {
    return <TrialExpiredModal onUpgrade={() => navigate('/upgrade')} accessType={trialInfo.access_type} />;
  }

  const isAdminGrant = trialInfo.access_type === 'admin_grant';

  return (
    <div className={`w-full py-2 px-4 flex items-center justify-between ${
      isUrgent ? 'bg-gradient-to-r from-red-600 to-orange-600' : isAdminGrant ? 'bg-gradient-to-r from-emerald-600 to-teal-600' : 'bg-gradient-to-r from-blue-600 to-purple-600'
    }`} data-testid="trial-banner">
      <div className="flex items-center space-x-3">
        <Clock className="h-5 w-5 text-white animate-pulse" />
        <span className="text-white font-medium text-sm" data-testid="trial-banner-label">
          {isAdminGrant ? '🔓 Acesso liberado pelo administrador:' : '🎁 Teste Grátis:'}
        </span>
        <div className="flex items-center space-x-1">
          <TimeBlock value={timeLeft.days} label="d" />
          <span className="text-white font-bold">:</span>
          <TimeBlock value={timeLeft.hours} label="h" />
          <span className="text-white font-bold">:</span>
          <TimeBlock value={timeLeft.minutes} label="m" />
          <span className="text-white font-bold">:</span>
          <TimeBlock value={timeLeft.seconds} label="s" />
        </div>
      </div>
      
      <button
        onClick={() => navigate('/upgrade')}
        className="bg-white text-purple-700 px-4 py-1 rounded-full text-sm font-bold hover:bg-yellow-400 hover:text-black transition-all flex items-center space-x-1"
      >
        <Crown className="h-4 w-4" />
        <span>FAZER UPGRADE</span>
      </button>
    </div>
  );
};

// Bloco de tempo individual
const TimeBlock = ({ value, label }) => (
  <div className="bg-white/20 rounded px-2 py-1 text-center min-w-[40px]">
    <span className="text-white font-bold text-lg">{String(value).padStart(2, '0')}</span>
    <span className="text-white/80 text-xs ml-1">{label}</span>
  </div>
);

// Modal de trial expirado (tela de bloqueio)
export const TrialExpiredModal = ({ onUpgrade, accessType }) => {
  const navigate = useNavigate();
  const isAdminGrant = accessType === 'admin_grant';

  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      price: 297,
      icon: <Star className="h-8 w-8" />,
      color: 'blue',
      features: [
        '50 links de afiliados',
        '25 conteúdos IA/mês',
        '9 plataformas',
        'Suporte por email'
      ]
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 797,
      icon: <Zap className="h-8 w-8" />,
      color: 'purple',
      popular: true,
      features: [
        '500 links de afiliados',
        '250 conteúdos IA/mês',
        'Todas as plataformas',
        'Automação de campanhas',
        'Suporte prioritário'
      ]
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 997,
      icon: <Crown className="h-8 w-8" />,
      color: 'yellow',
      features: [
        'Links ilimitados',
        'IA ilimitada',
        'Suporte dedicado 24/7',
        'White-label',
        'API personalizada'
      ]
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="max-w-4xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-500/20 rounded-full mb-4">
            <AlertTriangle className="h-10 w-10 text-red-500" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2" data-testid="trial-expired-title">
            {isAdminGrant ? 'Seu período de acesso liberado terminou ⏰' : 'Seu Período de Teste Expirou! ⏰'}
          </h1>
          <p className="text-gray-400 text-lg">
            {isAdminGrant
              ? 'Para continuar usando o MonetizeMaster, assine um plano mensal ou aguarde uma nova liberação do administrador:'
              : 'Para continuar usando o MonetizeMaster e não perder seus dados, escolha um plano:'}
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-white/10 backdrop-blur-lg rounded-2xl p-6 border-2 ${
                plan.popular ? 'border-purple-500 scale-105' : 'border-white/20'
              } hover:border-white/40 transition-all`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-purple-500 text-white px-4 py-1 rounded-full text-xs font-bold">
                  MAIS POPULAR
                </div>
              )}
              
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                plan.color === 'blue' ? 'bg-blue-500/20 text-blue-400' :
                plan.color === 'purple' ? 'bg-purple-500/20 text-purple-400' :
                'bg-yellow-500/20 text-yellow-400'
              }`}>
                {plan.icon}
              </div>
              
              <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
              
              <div className="mb-4">
                <span className="text-3xl font-bold text-white">R$ {plan.price}</span>
                <span className="text-gray-400">/mês</span>
              </div>
              
              <ul className="space-y-2 mb-6">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center text-sm text-gray-300">
                    <Check className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              
              <button
                onClick={() => navigate(`/checkout?plan=${plan.id}`)}
                className={`w-full py-3 rounded-lg font-bold transition-all ${
                  plan.popular
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                ESCOLHER {plan.name.toUpperCase()}
              </button>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-gray-500 text-sm">
            💳 Pagamento seguro | 🔒 Garantia de 7 dias | 📱 Acesso imediato
          </p>
        </div>
      </div>
    </div>
  );
};

// Hook para verificar trial
export const useTrialCheck = () => {
  const [isExpired, setIsExpired] = useState(false);
  const [trialInfo, setTrialInfo] = useState(null);

  useEffect(() => {
    const checkTrial = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await axios.get(`${API}/trial/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setTrialInfo(response.data);
        setIsExpired(response.data.trial_expired);
      } catch (error) {
        console.error('Error checking trial:', error);
      }
    };

    checkTrial();
  }, []);

  return { isExpired, trialInfo };
};

export default TrialBanner;
